import { FunctionComponent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import {
  PublicLobbyData,
  subscribeToPublicLobbies,
  signIn,
  findLobby,
} from "./Firebase";
import { GameMode } from "./Types";
import { getRankLabel } from "./services/FirebaseUsers";
import { resetLocalStore } from "./Utils";
import { setWipeTransition } from "./store/wipeTransitionSlice";

type LobbyBrowserProps = {
  onClose: () => void;
};

type FilterBet = "all" | "free" | "paid";

const LobbyBrowser: FunctionComponent<LobbyBrowserProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector((state) => state.user.profile);

  const [allLobbies, setAllLobbies] = useState<PublicLobbyData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const myUid = userProfile?.uid ?? "";
  const [error, setError] = useState("");
  const [filterBet, setFilterBet] = useState<FilterBet>("all");
  const [filterRating, setFilterRating] = useState<"all" | "near">("all");

  useEffect(() => {
    setIsLoading(true);
    const unsub = subscribeToPublicLobbies((data) => {
      setAllLobbies(data);
      setIsLoading(false);
    });
    return () => unsub();
  }, [userProfile?.uid]);

  // Фильтрация
  const lobbies = allLobbies.filter((l) => {
    if (filterBet === "free" && l.betAmount > 0) return false;
    if (filterBet === "paid" && l.betAmount === 0) return false;
    if (filterRating === "near" && userProfile) {
      const diff = Math.abs(l.hostRating - userProfile.rating);
      if (diff > 300) return false;
    }
    return true;
  });

  const handleJoin = async (lobby: PublicLobbyData) => {
    if (joiningId) return;
    // Защита от входа в комнату со ставкой > баланс
    if (lobby.betAmount > 0 && userProfile && userProfile.coins < lobby.betAmount) {
      setError(`Недостаточно средств. Ставка ${lobby.betAmount} ₽, у вас ${userProfile.coins} ₽`);
      return;
    }
    setJoiningId(lobby.id);
    setError("");
    try {
      const docRef = await findLobby(lobby.roomCode);
      if (!docRef) {
        setError("Комната недоступна или уже заполнена");
        setJoiningId(null);
        return;
      }
      resetLocalStore(dispatch, false);
      dispatch(setWipeTransition(true));
      navigate(`/${lobby.roomCode}`);
      onClose();
    } catch {
      setError("Ошибка подключения");
      setJoiningId(null);
    }
  };

  const formatTime = (ts: PublicLobbyData["createdAt"]): string => {
    if (!ts?.toDate) return "";
    const diff = Math.floor((Date.now() - ts.toDate().getTime()) / 1000);
    if (diff < 60) return `${diff}с`;
    return `${Math.floor(diff / 60)}м`;
  };

  return (
    <div className="Lobby-overlay" onClick={onClose}>
      <div className="Lobby-modal" onClick={(e) => e.stopPropagation()}>
        <div className="Lobby-header">
          <h2 className="Lobby-title">Открытые комнаты</h2>
          <button className="Lobby-close" onClick={onClose}>&#x2715;</button>
        </div>

        {/* Фильтры */}
        <div className="Lobby-filters">
          <div className="Lobby-filter-group">
            <span className="Lobby-filter-label">Ставка:</span>
            {(["all", "free", "paid"] as FilterBet[]).map((f) => (
              <button
                key={f}
                className={`Lobby-filter-btn ${filterBet === f ? "active" : ""}`}
                onClick={() => setFilterBet(f)}
              >
                {f === "all" ? "Все" : f === "free" ? "Без ставки" : "Со ставкой"}
              </button>
            ))}
          </div>
          <div className="Lobby-filter-group">
            <span className="Lobby-filter-label">Рейтинг:</span>
            <button
              className={`Lobby-filter-btn ${filterRating === "all" ? "active" : ""}`}
              onClick={() => setFilterRating("all")}
            >Все</button>
            <button
              className={`Lobby-filter-btn ${filterRating === "near" ? "active" : ""}`}
              onClick={() => setFilterRating("near")}
            >Близкий рейтинг</button>
          </div>
        </div>

        {error && <div className="Lobby-error">{error}</div>}

        {isLoading ? (
          <div className="Lobby-loading">
            <div className="Lobby-spinner" /><span>Загрузка...</span>
          </div>
        ) : lobbies.length === 0 ? (
          <div className="Lobby-empty">
            <div className="Lobby-empty-icon" />
            <p>Нет подходящих комнат</p>
            <span>Создайте игру — другие смогут присоединиться</span>
          </div>
        ) : (
          <div className="Lobby-list">
            <div className="Lobby-list-header">
              <span>Игрок</span>
              <span>Рейтинг</span>
              <span>Вид</span>
              <span>До побед</span>
              <span>Ставка</span>
              <span></span>
            </div>
            {lobbies.map((lobby) => (
              <div key={lobby.id} className="Lobby-row">
                <div className="Lobby-row-player">
                  <span className="Lobby-row-nickname">{lobby.hostNickname}</span>
                  <span className="Lobby-row-rank">{getRankLabel(lobby.hostRating)}</span>
                  <span className="Lobby-row-time">{formatTime(lobby.createdAt)} назад</span>
                </div>
                <span className="Lobby-row-rating">{lobby.hostRating}</span>
                <span className="Lobby-row-mode-wins">
                  <span className={`Lobby-row-mode ${lobby.gameMode === GameMode.LongNardy ? "long" : "short"}`}>
                    {lobby.gameMode === GameMode.LongNardy ? "Длинные" : "Короткие"}
                  </span>
                  <span className="Lobby-row-points">до {lobby.pointsToWin}</span>
                </span>
                <span className={`Lobby-row-bet ${lobby.betAmount > 0 ? "has-bet" : ""}`}>
                  {lobby.betAmount > 0 ? `${lobby.betAmount} ₽` : "–"}
                </span>
                {lobby.hostUid === myUid ? (
                  <span className="Lobby-own-badge">Ваша</span>
                ) : (
                  <button
                    className="Lobby-join-button"
                    onClick={() => handleJoin(lobby)}
                    disabled={joiningId === lobby.id}
                  >
                    {joiningId === lobby.id
                      ? <span className="Lobby-spinner small" />
                      : "Войти"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LobbyBrowser;
